import React, { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Close, DeleteOutline, EditOutlined } from "@mui/icons-material";
import { MenuItem, Typography } from "@mui/material";

import { deleteUserAction, fetchUsers } from "actions/userActions";
import { SnackBarWithInfoAlert } from "components/Alerts/AlertSnackbar";
import BurgerMenu from "components/Menu/BurgerMenu";
import { ModelDialog } from "components/Model";
import DataGridTable from "components/Table/DataGrid";
import HiddenOn from "components/Wrappers/HiddenOn";
import AppRoleContext from "context/AppRoleContext";
import { roleConstants, isBuilderAdmin } from "core/utils/roleUtils";

import { getTotalUsersCount, getUsersList } from "selectors/userSelector";
import EditUsers from "./EditUsers";
import { getUserProfileDetails } from "selectors/appSelector";
import {
  AddNewUser,
  ChangeUserRole,
  DeleteUserConfirmation,
} from "./Users_ConfirmationDialogs";

export const UsersList = () => {
  const dispatch = useDispatch();
  const appRoleDetails = useContext(AppRoleContext);

  const usersList = useSelector(getUsersList);
  const profileDetails = useSelector(getUserProfileDetails);

  const totalCount = useSelector(getTotalUsersCount);
  const [newUserModel, setNewUserModel] = useState(false);
  const [toast, setToastMessage] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [pageModel, setPageModel] = React.useState({ page: 0, pageSize: 10 });

  const [editUser, setEditUser] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  const [deleteUserSelection, setDeleteUserSelection] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showChangeRoleConfirmation, setShowChangeRoleConfirmation] =
    useState(false);

  const fetchUsersList = useCallback(() => {
    dispatch(fetchUsers({ ...pageModel, pageNumber: pageModel.page + 1 }));
  }, [dispatch, pageModel]);

  useEffect(() => {
    fetchUsersList();
  }, [fetchUsersList]);

  const columns = [
    {
      field: "userId",
      headerName: "User ID",
      width: 100,
    },
    {
      field: "firstName",
      headerName: "First Name",
      width: 200,
      valueFormatter: (_, row) => {
        return `${row.firstName} ${row.lastName}`;
      },
      // ,
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
    },
    {
      field: "mobileNumber",
      headerName: "Contact Number",
      width: 130,
    },
    {
      field: "userType",
      headerName: "Current Role",
      width: 130,
      valueFormatter: (_, user) => {
        const { role = {} } = user;
        return `${role?.applUserType}`;
      },
    },
    {
      key: "changeRole",
      headerName: "",
      width: 200,
      renderCell: (params) => {
        return (
          <Typography className="body1">
            <button
              className="primaryButton"
              onClick={() => {
                setShowChangeRoleConfirmation(true);
                setEditUser(params.row);
              }}
            >
              Manage Role
            </button>
          </Typography>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      flex: 1,
      width: 100,
      align: "right",
      getActions: (params) => [
        <BurgerMenu>
          <MenuItem
            onClick={() => {
              setShowEdit(true);
              setEditUser(params.row);
            }}
          >
            <EditOutlined /> <p className="m-0">Edit Details</p>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setShowDeleteConfirmation(true);
              setDeleteUserSelection(params.row);
            }}
            // disabled
          >
            <DeleteOutline /> <p className="m-0">Remove User</p>
          </MenuItem>
        </BurgerMenu>,
      ],
    },
  ];

  return (
    <>
      <ModelDialog
        open={showEdit}
        handleClose={() => {
          setShowEdit(false);
          setEditUser(null);
        }}
        dialogContent={
          <EditUsers
            userDetails={editUser}
            onCancel={() => {
              setShowEdit(false);
              setEditUser(null);
            }}
            onUpdateSuccess={() => {
              fetchUsersList();
              setShowEdit(false);
              setEditUser(null);
            }}
          />
        }
      />
      <SnackBarWithInfoAlert
        open={showToast}
        alertSeverity={toast.status}
        alertMessage={toast.message}
        handleClose={() => {
          setShowToast(false);
          setToastMessage({});
        }}
      />
      <ModelDialog
        open={newUserModel}
        dialogContent={
          <AddNewUser
            setNewUserModel={setNewUserModel}
            onInviteSuccess={(payload) => {
              setShowToast(true);
              setToastMessage(payload);
            }}
          />
        }
        handleClose={() => setNewUserModel(false)}
      />
      <ModelDialog
        open={showDeleteConfirmation}
        handleClose={() => setShowDeleteConfirmation(false)}
        dialogContent={
          <DeleteUserConfirmation
            onConfirmDelete={() => {
              dispatch(
                deleteUserAction(deleteUserSelection.userId, () => {
                  fetchUsersList();
                  setShowDeleteConfirmation(false);
                  setDeleteUserSelection(null);
                  setToastMessage({
                    status: "success",
                    message: "User deleted successfully",
                  });
                  setShowToast(true);
                })
              );
            }}
            onCancel={() => setShowDeleteConfirmation(false)}
            deleteUserSelection={deleteUserSelection}
          />
        }
      />
      <ModelDialog
        open={showChangeRoleConfirmation}
        handleClose={() => setShowChangeRoleConfirmation(false)}
        dialogContent={
          <ChangeUserRole
            onCancel={(isUpdated) => {
              setShowChangeRoleConfirmation(false);
              setEditUser(null);
              if (isUpdated) {
                fetchUsersList();
              }
            }}
            toast={toast}
            setToastMessage={setToastMessage}
            showToast={showToast}
            setShowToast={setShowToast}
            setEditUser={setEditUser}
            selectedUser={editUser}
          />
        }
      />

      <div className="user-list-container p-2">
        <div className="user-list-header d-flex justify-content-between">
          <div>
            <Typography className="h1">Users</Typography>
            <Typography className="body1">
              Showing list of all the users associated with this account
            </Typography>
          </div>
          <HiddenOn
            isHidden={
              appRoleDetails.roleName === roleConstants.SYSTEM_ADMIN ||
              !isBuilderAdmin(appRoleDetails)
            }
          >
            <div className="align-self-center">
              <button
                className="primaryButton mt-3"
                onClick={() => {
                  setNewUserModel(true);
                }}
              >
                Add New User
              </button>
            </div>
          </HiddenOn>
        </div>
        <div className="user-list-section mt-3 p-3">
          <DataGridTable
            columns={columns}
            data={usersList.filter(
              (user) => user.userId !== profileDetails.userId
            )}
            defineRowId={(row) => row.userId}
            checkboxSelection={false}
            showToolbar={false}
            pagination={totalCount > 10}
            totalCount={totalCount}
            paginationModel={pageModel}
            onPaginationChange={(props) => {
              setPageModel(props);
            }}
          />
        </div>
      </div>
    </>
  );
};

export default UsersList;
